import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import {
  STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX,
  normalizeHexColor,
  rgbToHex,
} from '../../StoreCheckoutColorPicker/storeCheckoutColorPicker.utils';
import {
  STORE_CHECKOUT_BRANDING,
  STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES,
  STORE_LOGO_SOURCE,
  STORE_PRIMARY_COLOR_SOURCE,
} from '../../constants/storeCheckout.constants';
import type { IStoreCustomizationBrandingState } from '../../helpers/storeCheckoutCustomizationPayload.utils';
import type { IDesignStoreFormShape } from '../storeDetails.types';
import {
  applyDefaultFreshPrintBrandingToForm,
  isRemoteStoreLogoSrc,
  isUserUploadedStoreLogo,
} from '../../helpers/storeCheckoutUiVariationBranding.utils';
import {
  getStoreLogoUploadErrorMessage,
  loadAndValidateStoreLogoFile,
  validateStoreLogoDataUrl,
} from './storeLogoUpload.utils';
import type { StorePrimaryColorSource } from '@/common/interfaces/store.interface';
import { showToastrMessage } from '@/utilities/Toastr.Utils';

interface IUseStoreBrandingParams {
  designForm: UseFormReturn<IDesignStoreFormShape>;
  onCustomizationChange?: (branding: IStoreCustomizationBrandingState) => void;
  uploadLogoToS3?: (file: File) => Promise<string | null>;
  savedBranding?: IStoreCustomizationBrandingState | null;
}

export const useStoreBranding = ({
  designForm,
  onCustomizationChange,
  uploadLogoToS3,
  savedBranding = null,
}: IUseStoreBrandingParams) => {
  const { watch, setValue } = designForm;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extractedColors, setExtractedColors] = useState<string[]>([]);
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [primaryColorSource, setPrimaryColorSource] = useState<StorePrimaryColorSource>(
    STORE_PRIMARY_COLOR_SOURCE.PRESET
  );
  const [storeLogoFileName, setStoreLogoFileName] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerInitialHex, setColorPickerInitialHex] = useState(
    STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX
  );
  const [logoUploadErrorMessage, setLogoUploadErrorMessage] = useState('');
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const hasValidatedInitialLogoRef = useRef(false);
  const lastExtractedLogoSrcRef = useRef<string | null>(null);
  const logoUploadProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasAppliedSavedBrandingRef = useRef(false);

  useEffect(() => {
    if (!savedBranding || hasAppliedSavedBrandingRef.current) {
      return;
    }

    hasAppliedSavedBrandingRef.current = true;
    setValue('selectedColor', savedBranding.selectedColor);
    setPrimaryColorSource(savedBranding.primaryColorSource);
    setCustomColors(savedBranding.customColors);
    setStoreLogoFileName(savedBranding.storeLogoFileName);
  }, [savedBranding, setValue]);

  const storeLogo = (watch('storeLogo') as string) ?? '';
  const selectedColor = (watch('selectedColor') as string) ?? '';

  const buildBrandingState = useCallback(
    (overrides?: Partial<IStoreCustomizationBrandingState>): IStoreCustomizationBrandingState => ({
      selectedColor: overrides?.selectedColor ?? selectedColor,
      primaryColorSource: overrides?.primaryColorSource ?? primaryColorSource,
      customColors: overrides?.customColors ?? customColors,
      storeLogoFileName: overrides?.storeLogoFileName ?? storeLogoFileName,
      storeLogoSource:
        overrides?.storeLogoSource ??
        (storeLogoFileName ? STORE_LOGO_SOURCE.UPLOADED : STORE_LOGO_SOURCE.DEFAULT),
    }),
    [customColors, primaryColorSource, selectedColor, storeLogoFileName]
  );

  const persistCustomization = useCallback(
    (overrides?: Partial<IStoreCustomizationBrandingState>) => {
      onCustomizationChange?.(buildBrandingState(overrides));
    },
    [buildBrandingState, onCustomizationChange]
  );

  const clearLogoUploadProgressInterval = useCallback(() => {
    if (logoUploadProgressIntervalRef.current) {
      clearInterval(logoUploadProgressIntervalRef.current);
      logoUploadProgressIntervalRef.current = null;
    }
  }, []);

  const clearLogoUploadError = useCallback(() => {
    setLogoUploadErrorMessage('');
  }, []);

  const setLogoUploadError = useCallback(
    (error: 'invalid_file_type' | 'invalid_dimensions' | 'unable_to_load') => {
      setLogoUploadErrorMessage(getStoreLogoUploadErrorMessage(error));
    },
    []
  );

  const extractColorsFromImage = useCallback((imageDataUrl: string) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onerror = () => {
      setExtractedColors([]);
    };
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const maxSize = 100;
      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      const colorMap: Record<string, number> = {};

      for (let i = 0; i < pixels.length; i += 16) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const a = pixels[i + 3];
        if (a >= 128) {
          const brightness = (r + g + b) / 3;
          if (brightness <= 240 && brightness >= 30) {
            const rr = Math.round(r / 32) * 32;
            const gg = Math.round(g / 32) * 32;
            const bb = Math.round(b / 32) * 32;
            const hex = rgbToHex(rr, gg, bb);
            colorMap[hex] = (colorMap[hex] || 0) + 1;
          }
        }
      }

      const sortedColors = Object.entries(colorMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6)
        .map(([color]) => color);

      setExtractedColors(sortedColors);
    };
    img.src = imageDataUrl;
  }, []);

  const handleLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const file = input.files?.[0];
      input.value = '';

      if (!file) {
        return;
      }

      clearLogoUploadProgressInterval();
      setIsLogoUploading(true);
      setLogoUploadProgress(0);
      clearLogoUploadError();

      logoUploadProgressIntervalRef.current = setInterval(() => {
        setLogoUploadProgress((currentProgress) =>
          currentProgress >= 90 ? 90 : currentProgress + 10
        );
      }, 150);

      try {
        const result = await loadAndValidateStoreLogoFile(file);

        if (!result.ok) {
          setLogoUploadError(result.error);
          if (result.error === 'invalid_file_type' || result.error === 'unable_to_load') {
            showToastrMessage(getStoreLogoUploadErrorMessage(result.error), 'failure');
          }
          return;
        }

        setLogoUploadProgress(100);
        setValue('storeLogo', result.dataUrl);
        extractColorsFromImage(result.dataUrl);

        if (uploadLogoToS3) {
          const uploadedFileName = await uploadLogoToS3(file);
          if (!uploadedFileName) {
            showToastrMessage('Failed to upload store logo. Please try again.', 'failure');
            setValue('storeLogo', '');
            setExtractedColors([]);
            return;
          }

          setStoreLogoFileName(uploadedFileName);
          setValue('selectedColor', STORE_CHECKOUT_BRANDING.DEFAULT_FP_BRAND_COLOR);
          setPrimaryColorSource(STORE_PRIMARY_COLOR_SOURCE.PRESET);
          setCustomColors([]);
          persistCustomization({
            selectedColor: STORE_CHECKOUT_BRANDING.DEFAULT_FP_BRAND_COLOR,
            primaryColorSource: STORE_PRIMARY_COLOR_SOURCE.PRESET,
            customColors: [],
            storeLogoFileName: uploadedFileName,
            storeLogoSource: STORE_LOGO_SOURCE.UPLOADED,
          });
        }
      } finally {
        clearLogoUploadProgressInterval();
        setIsLogoUploading(false);
        setLogoUploadProgress(0);
      }
    },
    [
      clearLogoUploadError,
      clearLogoUploadProgressInterval,
      extractColorsFromImage,
      persistCustomization,
      setLogoUploadError,
      setValue,
      uploadLogoToS3,
    ]
  );

  useEffect(
    () => () => {
      clearLogoUploadProgressInterval();
    },
    [clearLogoUploadProgressInterval]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveLogo = useCallback(() => {
    setExtractedColors([]);
    setStoreLogoFileName(null);
    clearLogoUploadError();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    applyDefaultFreshPrintBrandingToForm(designForm, true);
    setPrimaryColorSource(STORE_PRIMARY_COLOR_SOURCE.PRESET);
    setCustomColors([]);
    persistCustomization({
      selectedColor: designForm.getValues('selectedColor') ?? '',
      primaryColorSource: STORE_PRIMARY_COLOR_SOURCE.PRESET,
      customColors: [],
      storeLogoFileName: null,
      storeLogoSource: STORE_LOGO_SOURCE.DEFAULT,
    });
  }, [clearLogoUploadError, designForm, persistCustomization]);

  useEffect(() => {
    if (hasValidatedInitialLogoRef.current || !storeLogo) {
      return;
    }
    hasValidatedInitialLogoRef.current = true;

    if (isRemoteStoreLogoSrc(storeLogo)) {
      return;
    }

    validateStoreLogoDataUrl(storeLogo).then((result) => {
      if (!result.ok) {
        setValue('storeLogo', '');
        setExtractedColors([]);
        setLogoUploadError(result.error);
      }
    });
  }, [setLogoUploadError, setValue, storeLogo]);

  useEffect(() => {
    if (!isUserUploadedStoreLogo(storeLogo)) {
      lastExtractedLogoSrcRef.current = null;
      setExtractedColors([]);
      return;
    }

    if (lastExtractedLogoSrcRef.current === storeLogo) {
      return;
    }

    lastExtractedLogoSrcRef.current = storeLogo;
    extractColorsFromImage(storeLogo);
  }, [extractColorsFromImage, storeLogo]);

  const selectColor = useCallback(
    (color: string, source: StorePrimaryColorSource = STORE_PRIMARY_COLOR_SOURCE.PRESET) => {
      setValue('selectedColor', color);
      setPrimaryColorSource(source);
      persistCustomization({ selectedColor: color, primaryColorSource: source });
    },
    [persistCustomization, setValue]
  );

  const openColorPicker = useCallback(() => {
    const existingCustom = customColors[0];
    setColorPickerInitialHex(
      existingCustom
        ? normalizeHexColor(existingCustom) ?? STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX
        : STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX
    );
    setShowColorPicker(true);
  }, [customColors]);

  const closeColorPicker = useCallback(() => {
    setShowColorPicker(false);
  }, []);

  const addCustomColorFromPicker = useCallback(
    (hex: string) => {
      const formattedHex = normalizeHexColor(hex);
      if (!formattedHex) {
        return;
      }
      setCustomColors([formattedHex]);
      setValue('selectedColor', formattedHex);
      setPrimaryColorSource(STORE_PRIMARY_COLOR_SOURCE.CUSTOM);
      setShowColorPicker(false);
      persistCustomization({
        selectedColor: formattedHex,
        primaryColorSource: STORE_PRIMARY_COLOR_SOURCE.CUSTOM,
        customColors: [formattedHex],
      });
    },
    [persistCustomization, setValue]
  );

  const removeCustomColor = useCallback(
    (hex: string) => {
      const removedHex = normalizeHexColor(hex);
      if (!removedHex) {
        return;
      }

      const normalizedSelected = normalizeHexColor(selectedColor);
      const wasSelected = normalizedSelected?.toUpperCase() === removedHex.toUpperCase();

      setCustomColors([]);
      setShowColorPicker(false);

      if (wasSelected) {
        const fallback = extractedColors[0] ?? STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES[0] ?? '';
        if (fallback) {
          const fallbackSource = extractedColors.includes(fallback)
            ? STORE_PRIMARY_COLOR_SOURCE.EXTRACTED
            : STORE_PRIMARY_COLOR_SOURCE.PRESET;
          setValue('selectedColor', fallback);
          setPrimaryColorSource(fallbackSource);
          persistCustomization({
            selectedColor: fallback,
            primaryColorSource: fallbackSource,
            customColors: [],
          });
          return;
        }
      }

      persistCustomization({ customColors: [] });
    },
    [extractedColors, persistCustomization, selectedColor, setValue]
  );

  return {
    fileInputRef,
    storeLogo,
    selectedColor,
    extractedColors,
    customColors,
    showColorPicker,
    colorPickerInitialHex,
    logoUploadErrorMessage,
    isLogoUploading,
    logoUploadProgress,
    showStoreLogoRemoveButton: isUserUploadedStoreLogo(storeLogo),
    openColorPicker,
    closeColorPicker,
    addCustomColorFromPicker,
    removeCustomColor,
    handleLogoUpload,
    handleUploadClick,
    handleRemoveLogo,
    selectColor,
  };
};
